import {
    doc,
    updateDoc,
    getDoc,
    deleteDoc,
    serverTimestamp,
    addDoc,
    collection,
    deleteField,
} from 'firebase/firestore';

import { conformFormationData } from '@labradorsports/play-rendering';
import { getFolder, getFolderParent, processPlay, safeToDate } from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { DataTags, UnsavedPlayMessage } from '../../shared/constants.js';
import { downloadFile } from '../../shared/utils.js';
import { createApiSlice } from '../api.js';
import { mainActions } from '../main/index.js';
import { playEditorActions } from '../play-editor/index.js';
import { routerActions } from '../router/index.js';
import { RootState, RouterSelectors, Selectors } from '../state.js';
import { CustomMutationDefinition, CustomQueryDefinition, TD } from '../types.js';

import { playbookActions } from './index.js';

const checkOpenPlay = (plays: any[], shared: any[]) => {
    return (dispatch: TD, getState: () => RootState): void => {
        const state = getState();

        // If the currently opened play is saved and does not exist in the new playlist, close it
        if (state.playEditor.play) {
            const foundShared = shared?.find((pl: any) => pl.id === state.playEditor.play.id);

            const found = plays?.find((pl: any) => pl.id === state.playEditor.play.id);

            const communityPlayId = RouterSelectors.communityPlayId(state);
            const publicLinkId = RouterSelectors.publicLinkId(state);

            if (
                !found &&
                plays &&
                !foundShared &&
                shared &&
                !state.playEditor.play.id.includes('__') &&
                (!communityPlayId || !communityPlayId.includes(state.playEditor.play?.id)) &&
                !publicLinkId
            ) {
                dispatch(playEditorActions.PlayLoaded());
            }
        }
    };
};

function patchTeamPlaybookFromSavedPlay(dispatch: TD, getState: () => RootState, play: any) {
    const state = getState();
    const myPlaybook = Selectors.myPlaybook(state);
    const activeTeam = Selectors.activeTeam(state);
    const teamIds = [
        ...new Set(
            (Selectors.allTeams(state) ?? [])
                .map((team) => team.id)
                .concat(activeTeam)
                .filter(Boolean)
        ),
    ];

    const formation = play.formationId
        ? myPlaybook?.formations?.find((entry) => entry.id === play.formationId)
        : undefined;
    const concepts = (play.concepts ?? [])
        .map((playConcept) => playConcept?.conceptId)
        .filter(Boolean)
        .map((conceptId) => myPlaybook?.concepts?.find((entry) => entry.id === conceptId))
        .filter(Boolean);

    teamIds.forEach((teamId) => {
        const teamPlaybook = playbookApi.endpoints.loadTeamPlaybook.select({ activeTeam: teamId })(state).data;

        if (!teamPlaybook?.shared?.some((playlistItem) => playlistItem.id === play.id)) {
            return;
        }

        dispatch(
            playbookApi.util.updateQueryData('loadTeamPlaybook', { activeTeam: teamId }, (draft) => {
                const index = draft.shared.findIndex((playlistItem) => playlistItem.id === play.id);

                Object.assign(draft.shared[index], play);

                if (formation) {
                    draft.formations = [
                        ...(draft.formations ?? []).filter((entry) => entry.id !== formation.id),
                        formation,
                    ];
                }

                if (concepts.length > 0) {
                    const conceptIds = new Set(concepts.map((concept) => concept.id));
                    draft.concepts = [
                        ...(draft.concepts ?? []).filter((entry) => !conceptIds.has(entry.id)),
                        ...concepts,
                    ];
                }
            })
        );
    });
}

function playbookTags(playbook: any, backupId: string) {
    const listTags = [
        { type: DataTags.PLAYLIST_ITEM, id: `${backupId}_LIST` },
        { type: DataTags.FORMATION, id: `${backupId}_LIST` },
        { type: DataTags.CONCEPT, id: `${backupId}_LIST` },
        { type: DataTags.FOLDER, id: `${backupId}_LIST` },
    ];

    if (!playbook) {
        return listTags;
    }

    const pbListTags = [
        { type: DataTags.PLAYLIST_ITEM, id: `${playbook.id}_LIST` },
        { type: DataTags.FORMATION, id: `${playbook.id}_LIST` },
        { type: DataTags.CONCEPT, id: `${playbook.id}_LIST` },
        { type: DataTags.FOLDER, id: `${playbook.id}_LIST` },
    ];

    const playTags = (playbook.plays ?? playbook.shared).map((play) => ({
        type: DataTags.PLAYLIST_ITEM,
        id: `${playbook.id}_${play.id}`,
    }));
    const formationTags = (playbook.formations ?? []).map((formation) => ({
        type: DataTags.FORMATION,
        id: `${playbook.id}_${formation.id}`,
    }));
    const conceptTags = (playbook.concepts ?? []).map((concept) => ({
        type: DataTags.CONCEPT,
        id: `${playbook.id}_${concept.id}`,
    }));
    const folderTags = playbook.folders.map((folder) => ({ type: DataTags.FOLDER, id: `${playbook.id}_${folder.id}` }));

    return [...playTags, ...formationTags, ...conceptTags, ...folderTags, ...listTags, ...pbListTags];
}

export const playbookApi = createApiSlice({
    loadMyPlaybook: {
        type: 'query',
        extraOptions: {
            showLoading: true,
        },
        query: (arg, { extra }) => ({
            meta: {
                site: extra.site,
            },
            path: 'playbook/getMyPlaybook',
        }),
        transformResponse: (response, { site }) => {
            const playlist = response.plays.map((pl: any) => processPlay(pl, pl.id));

            const formations = response.formations.map((formation) =>
                conformFormationData(site.PlayConfig, {
                    ...formation,
                    createdDate: safeToDate(formation.createdDate),
                })
            );

            const concepts = (response.concepts ?? []).map((concept) => ({
                ...concept,
                createdDate: safeToDate(concept.createdDate),
            }));

            return {
                ...response,
                plays: playlist,
                formations,
                concepts,
            };
        },
        onQueryEnded: ({ data: playbook }, { dispatch, extra, getState }) => {
            if (playbook) {
                const state = getState();
                const { logger } = extra;
                const { plays, formations, folders } = playbook;
                const teamPlaybook = Selectors.teamPlaybook(state);

                logger.log('loadMyPlaybook complete', {
                    plays: plays?.length,
                    formations: formations?.length,
                    folders: folders?.length,
                    id: playbook.id,
                });

                dispatch(checkOpenPlay(playbook.plays, teamPlaybook?.shared));
            }
        },
        providesTags: (result) => {
            return playbookTags(result, 'MY');
        },
    } as CustomQueryDefinition<void, any>,

    loadTeamPlaybook: {
        type: 'query',
        isValid: ({ activeTeam }) => Boolean(activeTeam),
        query: ({ activeTeam }, { extra }) => {
            const { site } = extra;

            return {
                path: 'playbook/getTeamPlaybook',
                query: { teamId: activeTeam },
                meta: { site },
            };
        },
        transformResponse: (response, { site }) => {
            const shared = response.playbook.shared?.map((pl: any) => processPlay(pl, pl.id));

            const formations = response.playbook.formations.map((formation) =>
                conformFormationData(site.PlayConfig, {
                    ...formation,
                    createdDate: safeToDate(formation.createdDate),
                })
            );

            const concepts = (response.playbook.concepts ?? []).map((concept) => ({
                ...concept,
                createdDate: safeToDate(concept.createdDate),
            }));

            return {
                id: response.id,
                ...response.playbook,
                shared,
                formations,
                concepts,
            };
        },
        onQueryEnded: ({ data: playbook }, { dispatch, getState, extra }) => {
            if (playbook) {
                const state = getState();
                const { logger } = extra;
                const { shared, formations, folders, id } = playbook;
                const myPlaybook = Selectors.myPlaybook(state);

                logger.log('loadTeamPlaybook complete', {
                    id,
                    plays: shared?.length,
                    formations: formations?.length,
                    folders: folders?.length,
                });

                dispatch(checkOpenPlay(myPlaybook?.plays, shared));
            }
        },
        providesTags: (result: any, error, { activeTeam }) => {
            return playbookTags(result, activeTeam);
        },
    } as CustomQueryDefinition<{ activeTeam: string }, any>,

    loadViewedPlays: {
        type: 'query',
        isValid: ({ activeTeam }) => Boolean(activeTeam),
        query: ({ activeTeam, folderId }) => ({
            path: 'playbook/getViewedPlays',
            query: {
                teamId: activeTeam,
                folderId,
            },
        }),
        transformResponse: (response) => response.viewedPlayIds,
        providesTags: (result, error, { activeTeam, folderId }) => {
            const baseTags = [{ type: DataTags.VIEWED_PLAYS, id: `${activeTeam}_${folderId}` }];

            if (!result) {
                return baseTags;
            }

            const tags = result.map((id: string) => ({ type: DataTags.VIEWED_PLAYS, id: `${activeTeam}_${id}` }));

            return [...tags, ...baseTags];
        },
    } as CustomQueryDefinition<{ activeTeam: string; folderId: string }, any>,

    loadMyPlay: {
        type: 'query',
        isValid: ({ playbookId, playId }) => Boolean(playbookId && playId),
        queryFn: async ({ playbookId, playId }, { extra }) => {
            const { logger } = extra;

            const playRef = doc(firestore, 'playbooks', playbookId, 'plays', playId);
            const snapshot = await getDoc(playRef);

            logger.log('loadPlay complete');

            const logIfBad = (date) => logger.log('bad play date', { playId, date });

            const play = processPlay(snapshot.data(), playId, logIfBad);

            await updateDoc(playRef, {
                lastViewed: serverTimestamp(),
            });

            return { data: play };
        },
        providesTags: (result, error, { playbookId, playId }) => [
            { type: DataTags.PLAY, id: `${playbookId}_${playId}` },
        ],
    } as CustomQueryDefinition<{ playbookId: string; playId: string }, any>,

    loadSharedPlay: {
        type: 'query',
        query: ({ teamId, srcPlaybookId, playId }) => {
            return {
                path: 'playbook/getSharedPlay',
                query: {
                    teamId,
                    srcPlaybookId,
                    playId,
                },
            };
        },
        transformResponse: ({ play }) => play,
        providesTags: (result, error, { teamId, srcPlaybookId, playId }) => [
            { type: DataTags.PLAY, id: `${srcPlaybookId}_${playId}` },
        ],
        onQueryEnded: (result, { extra, dispatch }, { teamId, srcPlaybookId, playId }) => {
            dispatch(playbookApi.util.invalidateTags([{ type: DataTags.VIEWED_PLAYS, id: `${teamId}_${playId}` }]));
        },
    } as CustomQueryDefinition<{ teamId: string; srcPlaybookId: string; playId: string }, any>,

    deletePlay: {
        type: 'mutation',
        queryFn: async ({ playbookId, playId }, { getState, extra, dispatch }) => {
            const state = getState();
            const { logger } = extra;
            const { play } = state.playEditor;

            if (play?.id === playId) {
                dispatch(playEditorActions.PlayLoaded());
            }

            if (!playId?.includes('__')) {
                logger.log('deletePlay existing', { playbookId, playId });
                // Existing play

                if (playId) {
                    logger.log('deletePlay existing update playlist complete');
                    await deleteDoc(doc(firestore, 'playbooks', playbookId, 'plays', playId));

                    logger.log('deletePlay existing delete play complete');
                } else {
                    logger.log('deletePlay play without id');
                }
            }
        },
        onQueryEnded: async (result, { extra, getState, dispatch }, { playbookId, playId }) => {
            const { logger } = extra;
            const state = getState();
            const myPlaybook = Selectors.myPlaybook(state);
            const teamPlaybook = Selectors.teamPlaybook(state);
            const { folders } = [myPlaybook, teamPlaybook].find((pb) => pb.id === playbookId);

            await Promise.all(
                folders
                    .filter((f) => f.plays.includes(playId))
                    // This should filter down to one folder but just in case we clear all folders containing this play
                    .map(async (folder) => {
                        logger.log('updating containing folder', { id: folder.id });

                        await dispatch(
                            playbookApi.endpoints.saveFolder.initiate({
                                playbookId,
                                folder: {
                                    ...folder,
                                    plays: folder.plays.filter((p) => p !== playId),
                                },
                            })
                        );
                    })
            );
        },
        invalidatesTags: (result, error, { playbookId }) => [
            { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; playId: string }, any>,

    movePlaybookItems: {
        type: 'mutation',
        query: ({ changes, folderId, playbookId }) => ({
            path: 'playbook/movePlaybookItems',
            body: {
                playbookId,
                changes,
                folderId,
            },
        }),
        invalidatesTags: (result, error, { changes, folderId, playbookId }) => {
            return [
                { type: DataTags.FOLDER, id: `${playbookId}_${folderId}` },
                ...Object.keys(changes).flatMap((k) => [
                    { type: DataTags.FOLDER, id: `${playbookId}_${k}` },
                    { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_${k}` },
                    { type: DataTags.PLAY, id: `${playbookId}_${k}` },
                ]),
            ];
        },
    } as CustomMutationDefinition<{ changes: any; folderId?: string; playbookId: string }, any>,

    saveFolder: {
        type: 'mutation',
        isValid: ({ playbookId, folder }) => Boolean(playbookId && folder),
        extraOptions: {
            unloggableArg: ['folder'],
        },
        queryFn: async ({ playbookId, folder }) => {
            const newFolder: any = {
                ...folder,
            };

            if (folder.id) {
                await updateDoc(doc(firestore, 'playbooks', playbookId, 'folders', folder.id), newFolder);
            } else {
                const created = await addDoc(collection(firestore, 'playbooks', playbookId, 'folders'), newFolder);
                return {
                    data: created.id,
                };
            }

            return {
                data: '',
            };
        },
        invalidatesTags: (result, error, { playbookId, folder }) => [
            { type: DataTags.FOLDER, id: `${playbookId}_${folder.id ?? 'LIST'}` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; folder: Folder }, any>,

    deleteFolder: {
        type: 'mutation',
        isValid: ({ playbookId, folderId }) => Boolean(playbookId && folderId),
        queryFn: async ({ playbookId, folderId }) => {
            await deleteDoc(doc(firestore, 'playbooks', playbookId, 'folders', folderId));
        },
        onQueryEnded: async (result, { extra, getState, dispatch }, { folderId, playbookId }) => {
            const { logger } = extra;
            const state = getState();
            const myPlaybook = Selectors.myPlaybook(state);
            const teamPlaybook = Selectors.teamPlaybook(state);
            const { folders } = [myPlaybook, teamPlaybook].find((pb) => pb.id === playbookId);

            const deletedFolder = getFolder(folders, folderId);
            const parentFolder = getFolderParent(folders, folderId);

            if (parentFolder) {
                logger.log('updating parent folder', { id: parentFolder.id });

                await dispatch(
                    playbookApi.endpoints.saveFolder.initiate({
                        playbookId,
                        folder: {
                            ...parentFolder,
                            folders: parentFolder.folders
                                .filter((f) => f !== folderId)
                                .concat(deletedFolder.folders ?? []),
                            plays: parentFolder.plays.concat(deletedFolder.plays ?? []),
                        },
                    })
                );
            }
        },
        invalidatesTags: (result, error, { playbookId }) => [{ type: DataTags.FOLDER, id: `${playbookId}_LIST` }],
    } as CustomMutationDefinition<{ playbookId: string; folderId: string }, any>,

    saveSharedPlay: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['play'],
        },
        isValid: ({ playbookId, activeTeam, play }) => Boolean(playbookId && activeTeam && play?.id),
        query: ({ playbookId, activeTeam, play }) => ({
            path: 'playbook/saveSharedPlay',
            body: {
                play,
                teamId: activeTeam,
                srcPlaybookId: playbookId,
            },
        }),
        onQueryStarted: ({ play }, { extra }) => {
            const { logger } = extra;
            logger.log('saveSharedPlay play id', { id: play.id });
        },
        onQueryEnded: (result, { dispatch, getState }, { playbookId, activeTeam, play }) => {
            // We update the query data directly to prevent a loading indicator for the loadSharedPlay query
            dispatch(
                playbookApi.util.updateQueryData(
                    'loadSharedPlay',
                    {
                        teamId: activeTeam,
                        srcPlaybookId: playbookId,
                        playId: play.id,
                    },
                    () => play
                )
            );
            patchTeamPlaybookFromSavedPlay(dispatch, getState, play);
            dispatch(playEditorActions.PlayModified(false));
        },
    } as CustomMutationDefinition<{ playbookId: string; activeTeam: string; play: any; autosave?: boolean }, any>,

    saveExistingPlay: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['play'],
        },
        isValid: ({ playbookId, play }) => Boolean(playbookId && play?.id),
        queryFn: async ({ playbookId, play }, { extra }) => {
            const { logger } = extra;
            logger.log('saveExistingPlay play id', { id: play.id });

            const { lastUpdated, createdDate, sharedOn, ...playData } = play;

            await updateDoc(doc(firestore, 'playbooks', playbookId, 'plays', play.id), {
                ...playData,
                new: deleteField(),
                lastUpdated: serverTimestamp(),
            });
        },
        onQueryEnded: (result, { dispatch, getState }, { playbookId, play }) => {
            // We update the query data directly to prevent a loading indicator for the loadMyPlay query
            dispatch(playbookApi.util.updateQueryData('loadMyPlay', { playbookId, playId: play.id }, () => play));
            dispatch(
                playbookApi.util.updateQueryData('loadMyPlaybook', undefined, (draft) => {
                    if (!draft?.plays) {
                        return;
                    }

                    const index = draft.plays.findIndex((playlistItem) => playlistItem.id === play.id);

                    if (index !== -1) {
                        Object.assign(draft.plays[index], play);
                    }
                })
            );
            patchTeamPlaybookFromSavedPlay(dispatch, getState, play);
            dispatch(playEditorActions.PlayModified(false));
        },
    } as CustomMutationDefinition<{ playbookId: string; play: any; autosave?: boolean }, any>,

    saveNewPlay: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['play'],
        },
        query: ({ play, playbookId }) => ({
            path: 'playbook/saveNewPlay',
            body: {
                play,
                playbookId,
            },
        }),
        onQueryEnded: ({ data: { playlistItem } }, { dispatch, extra }, { play, playbookId }) => {
            const { logger } = extra;
            logger.log('saveNewPlay complete', { id: playlistItem.id });

            dispatch(playEditorActions.NewPlaySaved(playlistItem.id));
            dispatch(
                playbookApi.util.updateQueryData('loadMyPlaybook', undefined, (draft) => {
                    if (!draft?.plays) {
                        return;
                    }

                    const savedPlay = {
                        ...play,
                        ...playlistItem,
                        id: playlistItem.id,
                    };

                    if (draft.plays.some((playlistItem) => playlistItem.id === savedPlay.id)) {
                        return;
                    }

                    draft.plays.push(processPlay(savedPlay, playlistItem.id));
                })
            );
            dispatch(
                playbookApi.util.updateQueryData('loadMyPlay', { playbookId, playId: playlistItem.id }, () => ({
                    ...play,
                    id: playlistItem.id,
                }))
            );
        },
        invalidatesTags: (result, error, { playbookId }) => [
            { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; play: any; autosave?: boolean }, any>,

    shareItems: {
        type: 'mutation',
        query: ({ itemIds, modifications, proxyTeamId, srcPlaybookId }) => ({
            path: 'playbook/shareItems',
            body: {
                srcPlaybookId,
                modifications,
                itemIds,
                proxyTeamId,
            },
        }),
        invalidatesTags: (result, error, { modifications }) =>
            Object.keys(modifications).flatMap((k) => [
                { type: DataTags.PLAYLIST_ITEM, id: `${k}_LIST` },
                { type: DataTags.FOLDER, id: `${k}_LIST` },
            ]),
    } as CustomMutationDefinition<
        { itemIds: string[]; modifications: any; proxyTeamId?: string; srcPlaybookId: string },
        any
    >,

    loadViewerDetails: {
        type: 'query',
        query: ({ activeTeam, srcPlaybookId, playId }) => ({
            path: 'playbook/getViewerDetails',
            query: {
                teamId: activeTeam,
                srcPlaybookId,
                playId,
            },
        }),
    } as CustomQueryDefinition<{ activeTeam: string; srcPlaybookId: string; playId: string }, any>,

    savePlayMetadata: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['name', 'description'],
        },
        query: ({ playbookId, playId, name, tags, description }) => ({
            path: 'playbook/updatePlayMetadata',
            body: {
                playbookId,
                playId,
                name,
                tags,
                description,
            },
        }),
        onQueryEnded: (result, { dispatch, getState }, { playId }) => {
            const state = getState();
            const teamPlaybook = Selectors.teamPlaybook(state);

            if (teamPlaybook) {
                const playlistItem = teamPlaybook.shared.find((pl) => pl.id === playId);

                if (playlistItem) {
                    dispatch(
                        playbookApi.util.invalidateTags([
                            { type: DataTags.PLAYLIST_ITEM, id: `${teamPlaybook.id}_${playId}` },
                        ])
                    );
                }
            }
        },
        invalidatesTags: (result, error, { playbookId, playId }) => [
            { type: DataTags.PLAY, id: `${playbookId}_${playId}` },
            { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_${playId}` },
        ],
    } as CustomMutationDefinition<
        { playbookId: string; playId: string; name: string; tags: string[]; description: string },
        any
    >,

    copyPlay: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['copyName'],
        },
        query: ({ playbookId, playId, copyName, flipHorizontal, sourcePlaybookId }) => ({
            path: 'playbook/copyPlay',
            body: {
                playbookId,
                playId,
                sourcePlaybookId,
                copyName,
                flipHorizontal,
            },
        }),
        onQueryEnded: ({ data: { playlistItem } }: any, { dispatch }) => {
            dispatch(routerActions.OpenPlay(playlistItem.id));
        },
        invalidatesTags: (result, error, { playbookId }) => [
            { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<
        { playbookId: string; playId: string; copyName: string; flipHorizontal: boolean; sourcePlaybookId: string },
        any
    >,

    sendStudyReminder: {
        type: 'mutation',
        query: ({ plays, activeTeam }) => ({
            path: 'playbook/sendStudyReminder',
            body: {
                plays,
                teamId: activeTeam,
            },
        }),
        onQueryEnded: ({ data: result }, { dispatch }) => {
            if (result.success) {
                dispatch(mainActions.GenericAlert('Your team has been notified.'));
            } else {
                dispatch(mainActions.GenericAlert('There are no users joined to your team.'));
            }
        },
    } as CustomMutationDefinition<{ plays: string[]; activeTeam: string }, any>,

    getSharedStatus: {
        type: 'query',
        query: ({ playId }, { getState }) => {
            const state = getState();
            const teams = Selectors.editableTeams(state);

            return {
                path: 'playbook/getSharedStatus',
                query: {
                    playId,
                    teamIds: teams.map((tm) => tm.id),
                },
            };
        },
    } as CustomQueryDefinition<{ playId: string }, any>,

    getPublicShareLink: {
        type: 'query',
        query: ({ playId, playbookId, teamId }) => ({
            path: 'playbook/getPublicShareLink',
            query: {
                playId,
                playbookId,
                teamId,
            },
        }),
        transformResponse: ({ link }) => link,
    } as CustomQueryDefinition<{ playId: string; playbookId: string; teamId: string }, any>,

    getPublicSharedPlay: {
        type: 'query',
        extraOptions: {
            anonymousHooks: true,
        },
        isValid: ({ publicLinkId }) => Boolean(publicLinkId),
        query: ({ publicLinkId }) => ({
            path: 'playbook/getPublicSharedPlay',
            query: {
                linkId: publicLinkId,
            },
        }),
        onQueryEnded: async ({ data: result }, { dispatch }) => {
            if (result?.success === false) {
                dispatch(playbookActions.PublicLoadPlayError(true));
            }
        },
    } as CustomQueryDefinition<{ publicLinkId: string }, any>,

    checkVideoStatus: {
        type: 'query',
        extraOptions: {
            anonymousHooks: true,
        },
        isValid: (arg) => Boolean(arg),
        query: ({ playbookId, playId, programId, linkId }) => ({
            path: 'playbook/checkVideoStatus',
            query: {
                playbookId,
                playId,
                programId,
                linkId,
            },
        }),
        onQueryEnded: ({ data: result }: any, { dispatch, getState }, { playId, playbookId }) => {
            const state = getState();
            const { previewUrl, videoUrl } = result;
            const videoDownload = Selectors.videoDownload(state, playId, playbookId);

            dispatch(
                playbookActions.AddVideoDownload({
                    ...videoDownload,
                    previewUrl,
                    videoUrl,
                })
            );
        },
    } as CustomQueryDefinition<{ playbookId: string; playId: string; programId: string; linkId?: string }, any>,

    triggerVideoExport: {
        type: 'mutation',
        extraOptions: {
            anonymousHooks: true,
        },
        query: ({ playbookId, playId, programId, linkId }) => ({
            path: 'playbook/exportVideo',
            query: {
                playbookId,
                playId,
                programId,
                linkId,
            },
        }),
        onQueryEnded: (
            { data: { estimatedWait, nonce } }: any,
            { dispatch, extra },
            { playbookId, playId, programId, name, linkId }
        ) => {
            const { logger } = extra;

            logger.log('export triggered', { estimatedWait, nonce });

            dispatch(
                playbookActions.AddVideoDownload({
                    playbookId,
                    playId,
                    programId,
                    nonce,
                    estimatedWait,
                    name,
                    linkId,
                })
            );
        },
    } as CustomMutationDefinition<
        { playbookId: string; playId: string; programId: string; name: string; linkId?: string },
        any
    >,

    getViewershipProfile: {
        type: 'query',
        isValid: ({ activeTeam, personUID }) => Boolean(activeTeam && personUID),
        query: ({ activeTeam, personUID }) => ({
            path: 'playbook/getViewershipProfile',
            query: {
                teamId: activeTeam,
                personUID,
            },
        }),
        transformResponse: ({ viewed }) => {
            return viewed
                .map((play) => ({
                    ...play,
                    viewers: Object.fromEntries(
                        Object.entries(play.viewers).map(([k, v]: [string, any]) => [
                            k,
                            {
                                ...v,
                                lastViewed: new Date(v.lastViewed),
                            },
                        ])
                    ),
                }))
                .sort((a, b) => b.viewCount - a.viewCount);
        },
    } as CustomQueryDefinition<{ activeTeam: string; personUID: string }, any>,

    saveFormation: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['formation'],
        },
        queryFn: async ({ playbookId, formation }, { extra }) => {
            const { logger } = extra;

            if (formation.id) {
                logger.log('existingFormation', { id: formation.id });

                await updateDoc(doc(firestore, 'playbooks', playbookId, 'formations', formation.id), {
                    ...formation,
                    playbookId,
                });

                return { data: { id: formation.id } };
            }

            logger.log('newFormation');

            const newFormation = {
                ...formation,
                createdDate: new Date(),
            };

            delete newFormation.id;
            delete newFormation.playbookId;

            const ref = await addDoc(collection(firestore, 'playbooks', playbookId, 'formations'), newFormation);

            return { data: { id: ref.id } };
        },
        invalidatesTags: (result, error, { playbookId, formation }) => [
            { type: DataTags.FORMATION, id: `${playbookId}_${formation.id ?? 'LIST'}` },
            { type: DataTags.FORMATION, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; formation: any }, any>,

    deleteFormation: {
        type: 'mutation',
        queryFn: async ({ playbookId, formationId }) => {
            await deleteDoc(doc(firestore, 'playbooks', playbookId, 'formations', formationId));
        },
        invalidatesTags: (result, error, { playbookId }) => [{ type: DataTags.FORMATION, id: `${playbookId}_LIST` }],
    } as CustomMutationDefinition<{ playbookId: string; formationId: string }, any>,

    saveConcept: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['concept'],
        },
        queryFn: async ({ playbookId, concept }, { extra }) => {
            const { logger } = extra;

            if (concept.id) {
                logger.log('existingConcept', { id: concept.id });

                await updateDoc(doc(firestore, 'playbooks', playbookId, 'concepts', concept.id), {
                    ...concept,
                    playbookId,
                });

                return { data: { id: concept.id } };
            }

            logger.log('newConcept');

            const newConcept = {
                ...concept,
                createdDate: new Date(),
            };

            delete newConcept.id;
            delete newConcept.playbookId;

            const ref = await addDoc(collection(firestore, 'playbooks', playbookId, 'concepts'), newConcept);

            return { data: { id: ref.id } };
        },
        invalidatesTags: (result, error, { playbookId, concept }) => [
            { type: DataTags.CONCEPT, id: `${playbookId}_${concept.id ?? 'LIST'}` },
            { type: DataTags.CONCEPT, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; concept: any }, any>,

    deleteConcept: {
        type: 'mutation',
        query: ({ playbookId, conceptId }) => ({
            path: 'playbook/deleteConcept',
            body: {
                playbookId,
                conceptId,
            },
        }),
        invalidatesTags: (result, error, { playbookId }) => [
            { type: DataTags.CONCEPT, id: `${playbookId}_LIST` },
            { type: DataTags.PLAYLIST_ITEM, id: `${playbookId}_LIST` },
        ],
    } as CustomMutationDefinition<{ playbookId: string; conceptId: string }, any>,

    searchFormations: {
        type: 'query',
        extraOptions: {
            unloggableArg: ['query'],
        },
        isValid: ({ query }) => query !== '',
        query: ({ playbookId, teamPlaybookId, query }) => ({
            path: 'search/formations',
            query: {
                query,
                playbookIds: [playbookId, teamPlaybookId].filter(Boolean),
            },
        }),
        transformResponse: (response) => response.results.hits,
    } as CustomQueryDefinition<{ playbookId: string; teamPlaybookId: string; query: string }, any>,

    downloadVideo: {
        type: 'mutation',
        extraOptions: {
            anonymousHooks: true,
        },
        queryFn: async ({ name, videoUrl }, { extra, dispatch }) => {
            const { logger } = extra;
            const filename = `${encodeURIComponent(name)}.mp4`;
            const url = `${videoUrl}&download=true`;

            await downloadFile(logger, filename, url);

            if (APP) {
                dispatch(mainActions.GenericAlert('The video was downloaded to your Files app.'));
            }
        },
    } as CustomMutationDefinition<{ name: string; videoUrl: string }, any>,

    confirmUnsaved: {
        type: 'mutation',
        extraOptions: {
            suppressError: true,
        },
        queryFn: async (arg, { dispatch, getState }) => {
            const state = getState();

            if (state.playEditor.playModified && !Selectors.playReadOnly(state)) {
                const result: boolean = await new Promise((resolve) => {
                    const confirm = window.confirm(UnsavedPlayMessage);
                    if (confirm) {
                        dispatch(playEditorActions.PlayModified(false));
                        dispatch(playEditorActions.Saving(false));
                        resolve(true);
                        return;
                    }

                    resolve(false);
                    return;
                });

                return { data: result };
            }

            return { data: true };
        },
    } as CustomMutationDefinition<void, any>,
});

export const {
    useLoadMyPlaybookQuery,
    useMovePlaybookItemsMutation,
    useSaveFolderMutation,
    useDeleteFolderMutation,
    useLoadViewedPlaysQuery,
    useLoadTeamPlaybookQuery,
    useSaveSharedPlayMutation,
    useSaveExistingPlayMutation,
    useSaveNewPlayMutation,
    useLoadSharedPlayQuery,
    useLoadMyPlayQuery,
    useDeletePlayMutation,
    useShareItemsMutation,
    useLoadViewerDetailsQuery,
    useSavePlayMetadataMutation,
    useCopyPlayMutation,
    useSendStudyReminderMutation,
    useGetSharedStatusQuery,
    useGetPublicShareLinkQuery,
    useGetPublicSharedPlayQuery,
    useCheckVideoStatusQuery,
    useTriggerVideoExportMutation,
    useGetViewershipProfileQuery,
    useSaveFormationMutation,
    useDeleteFormationMutation,
    useSaveConceptMutation,
    useDeleteConceptMutation,
    useSearchFormationsQuery,
    useDownloadVideoMutation,
    useConfirmUnsavedMutation,
} = playbookApi;
